import { Icon } from '../../components/icons/Icon'
import {
  TASK_PRIORITY_LABEL,
  TASK_STATUSES,
  TASK_STATUS_LABEL,
  type TaskPriority,
  type TaskView,
} from '../../types/task'
import type { CaseContext } from './useCaseContexts'
import './TaskBoard.css'

const PRIORITY_VARIANT: Record<TaskPriority, 'danger' | 'warning' | 'neutral'> = {
  high: 'danger',
  medium: 'warning',
  low: 'neutral',
}

export interface TaskBoardProps {
  tasks: TaskView[]
  caseContexts: Map<string, CaseContext>
  onCardClick: (task: TaskView) => void
}

function formatDueDate(dueDate: string | null): string | null {
  if (!dueDate) return null
  const d = new Date(dueDate + 'T00:00:00')
  return Number.isNaN(d.getTime()) ? dueDate : d.toLocaleDateString()
}

/**
 * The Kanban board view (todo / in_progress / blocked / done columns). Cards are click-to-open,
 * not drag-and-drop (optional per spec) — clicking opens `TaskFormModal`, where the status chips
 * are the real, audited status-change action (`POST /tasks/{id}/transitions`), never a dead button.
 */
export function TaskBoard({ tasks, caseContexts, onCardClick }: TaskBoardProps) {
  return (
    <div className="iw-kanban" role="list" aria-label="Task board">
      {TASK_STATUSES.map((status) => {
        const columnTasks = tasks.filter((t) => t.status === status)
        return (
          <section key={status} className="iw-kanban-column" role="listitem" aria-label={TASK_STATUS_LABEL[status]}>
            <header className="iw-kanban-column__header">
              <span>{TASK_STATUS_LABEL[status]}</span>
              <span className="iw-kanban-column__count">{columnTasks.length}</span>
            </header>
            <div className="iw-kanban-column__body">
              {columnTasks.length === 0 ? (
                <p className="iw-kanban-empty">No tasks.</p>
              ) : (
                columnTasks.map((task) => {
                  const caseContext = task.caseId ? caseContexts.get(task.caseId) : undefined
                  const due = formatDueDate(task.dueDate)
                  return (
                    <button
                      key={task.id}
                      type="button"
                      className="iw-kanban-card"
                      onClick={() => onCardClick(task)}
                    >
                      <span className="iw-kanban-card__title">{task.title}</span>
                      <span className={`iw-pill iw-pill--${PRIORITY_VARIANT[task.priority]}`}>
                        {TASK_PRIORITY_LABEL[task.priority]}
                      </span>
                      {caseContext ? (
                        <span className="iw-kanban-card__meta">
                          {caseContext.accountName ?? 'Unknown account'} · {caseContext.caseLabel}
                        </span>
                      ) : task.caseId ? (
                        <span className="iw-kanban-card__meta">Case-linked</span>
                      ) : null}
                      <span className="iw-kanban-card__footer">
                        {task.assigneeUserId ? (
                          <span className="iw-kanban-card__assignee">
                            <Icon name="users" size={12} /> Assigned
                          </span>
                        ) : (
                          <span className="iw-kanban-card__assignee">Unassigned</span>
                        )}
                        {due ? (
                          <span className="iw-kanban-card__due">
                            <Icon name="clock" size={12} /> {due}
                          </span>
                        ) : null}
                      </span>
                    </button>
                  )
                })
              )}
            </div>
          </section>
        )
      })}
    </div>
  )
}
